import type { Dispatch, ReactNode, SetStateAction } from "react";
import type { EntityType, EventOption } from "../../types";
import { adoptFlow, hasActivityFlow, remapOptionIds, withoutFlow } from "../../utils/activityFlow";
import { StepFlowEditor } from "./StepFlowEditor";
import { newOptionId } from "../../utils/newOptionId";

interface EventOptionsEditorProps {
  options: EventOption[];
  setOptions: Dispatch<SetStateAction<EventOption[]>>;
  /** Whose flow a list without wiring of its own borrows. */
  flowDefaults: EventOption[];
  /** What an empty list stands for, and what "Reset" copies in. */
  defaultsToLoad: EventOption[];
  /** The type, as read: "TRANSPORTATION" → shown in the hints and buttons. */
  typeName: string;
  hint: ReactNode;
  /** The type, as stored, which decides the special step kinds offered. */
  entityType?: EntityType | string | null;
}

/**
 * The log steps and their flow — the one-tap steps offered when logging. Used
 * for a place's own steps (EntityForm) and for a type's defaults (the type's
 * settings page). See StepFlowEditor for the list itself.
 */
export function EventOptionsEditor({
  options,
  setOptions,
  flowDefaults,
  defaultsToLoad,
  typeName,
  hint,
  entityType,
}: EventOptionsEditorProps) {
  // Three starting points, all shown as the flow that actually runs:
  //  - no steps of its own: it uses the defaults, and the first change copies
  //    them in;
  //  - its own steps but no wiring (every place carried over from the old
  //    database): it borrows the type's flow by step name, and the first
  //    change writes that flow onto it;
  //  - its own wired flow.
  const inherited = options.length === 0;
  const borrowsFlow = !inherited && !hasActivityFlow(options);
  const shown = inherited ? defaultsToLoad : borrowsFlow ? adoptFlow(options, flowDefaults) : options;

  // Fresh ids so two entities never share one — `remapOptionIds` rewrites the
  // flow to match, so the entity arrives with the whole workflow intact.
  const reset = () => setOptions(remapOptionIds(defaultsToLoad, newOptionId));
  // Drop the entity's own wiring so it borrows the type's again.
  const followTypeFlow = () => setOptions(withoutFlow);

  return (
    <div className="form-field">
      <span className="form-label">Log Steps (quick-add events)</span>
      <span className="form-hint">{hint}</span>
      {inherited && shown.length > 0 && (
        <span className="form-hint event-flow-source">
          Using the {typeName} steps. Changing anything here gives this its own copy.
        </span>
      )}
      {borrowsFlow && (
        <span className="form-hint event-flow-source">
          Following the {typeName} flow. Changing the buttons here gives this a flow of its own.
        </span>
      )}
      {!inherited && !borrowsFlow && (
        <span className="form-hint event-flow-source">
          Has a flow of its own.{" "}
          <button type="button" className="btn-link" onClick={followTypeFlow}>
            Follow the {typeName} flow instead
          </button>
        </span>
      )}
      <StepFlowEditor options={shown} entityType={entityType} onChange={(next) => setOptions(next)} />
      {!inherited && defaultsToLoad.length > 0 && (
        <div className="event-option-actions">
          <button type="button" className="btn-link" onClick={reset}>
            Reset to the {typeName} steps
          </button>
        </div>
      )}
    </div>
  );
}
